'use client';

import { create } from 'zustand';
import { StoreInitializer } from '@/utils/store-middlewares/initializeFromRemote';
import { LOCAL_STORAGE_KEYS } from '@/constants/storage';
import { subscribeWithSelector } from 'zustand/middleware';
import { IS_RUNNING_SERVER_SIDE } from '@/config/app.config';
import { getServerSideLocalStorage } from '@/utils/ssrUtils';
import { useShallow } from 'zustand/react/shallow';
import { ClientApplication } from '@shopify/app-bridge/client';
export type UserDataStoreState = {
  appKey: string;
  appUrl: string;
  owner: string;
  host?: string;
  isLoggedIn: boolean;
  isLoading: boolean;
  shopifyApp?: ClientApplication;
};

export type UserDataStoreActions = {
  setAppUrl: (newAppUrl: string) => void;
  setOwner: (newOwner: string) => void;
  setAppKey: (newAppKey: string) => void;
  removeUserData: () => void;
  updateUserData: (userData: { appKey: string; appUrl: string; owner: string }) => void;
  setHost: (newHost: string) => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setShopifyApp: (shopifyApp: ClientApplication) => void;
};

export type UserDataStore = UserDataStoreState & UserDataStoreActions;

export const getLocalStorage = () => (IS_RUNNING_SERVER_SIDE ? getServerSideLocalStorage() : localStorage);

const getLocalStorageItem = (itemKey: string) => {
  const stringItem = getLocalStorage().getItem(itemKey) || '';
  if (stringItem === 'undefined' || stringItem === 'null') {
    return '';
  }

  return stringItem;
};

const setAppUrl = (appUrl: string) => {
  getLocalStorage().setItem(LOCAL_STORAGE_KEYS.appUrl, appUrl);
};

const removeAppUrl = () => {
  getLocalStorage().removeItem(LOCAL_STORAGE_KEYS.appUrl);
};

const getAppUrl = () => {
  return getLocalStorageItem(LOCAL_STORAGE_KEYS.appUrl);
};

const getOwner = () => {
  return getLocalStorageItem(LOCAL_STORAGE_KEYS.owner);
};

const setAppKey = (appKey: string) => {
  getLocalStorage().setItem(LOCAL_STORAGE_KEYS.appKey, appKey);
};

const setOwner = (appKey: string) => {
  getLocalStorage().setItem(LOCAL_STORAGE_KEYS.owner, appKey);
};

const removeAppKey = () => {
  getLocalStorage().removeItem(LOCAL_STORAGE_KEYS.appKey);
};

const removeOwner = () => {
  getLocalStorage().removeItem(LOCAL_STORAGE_KEYS.owner);
};

const getAppKey = () => {
  return getLocalStorageItem(LOCAL_STORAGE_KEYS.appKey);
};

const getHost = () => {
  return getLocalStorageItem(LOCAL_STORAGE_KEYS.host);
};

const setHost = (host: string) => {
  getLocalStorage().setItem(LOCAL_STORAGE_KEYS.host, host);
};

const removeHost = () => {
  getLocalStorage().removeItem(LOCAL_STORAGE_KEYS.host);
};

const store: StoreInitializer<UserDataStore> = (set) => ({
  appUrl: getAppUrl(),
  appKey: getAppKey(),
  owner: getOwner(),
  host: getHost(),
  isLoggedIn: false,
  isLoading: true,
  shopifyApp: undefined,
  setAppUrl: (newAppUrl) => {
    set(() => ({ appUrl: newAppUrl }));
    setAppUrl(newAppUrl);
  },
  setHost: (newHost) => {
    set(() => ({ host: newHost }));
    setHost(newHost);
  },
  removeHost: () => {
    set(() => ({ host: '' }));
    removeHost();
  },
  setOwner: (newOwner) => {
    set(() => ({ owner: newOwner }));
    setOwner(newOwner);
  },
  removeOwner: () => {
    set(() => ({ owner: '' }));
    removeOwner();
  },
  setAppKey: (newAppKey) => {
    set(() => ({ appKey: newAppKey }));
    setAppKey(newAppKey);
  },
  setIsLoggedIn: (isLoggedIn) => {
    set(() => ({ isLoggedIn }));
  },
  setIsLoading: (isLoading) => {
    set(() => ({ isLoading }));
  },
  setShopifyApp: (shopifyApp) => {
    set(() => ({ shopifyApp }));
  },
  removeUserData: () => {
    set(() => ({ appKey: '', appUrl: '', owner: '', host: '', isLoggedIn: false, isLoading: false }));
    removeAppKey();
    removeAppUrl();
    removeOwner();
    removeHost();
  },
  updateUserData: ({ appKey, appUrl, owner }) => {
    set(() => ({ appKey, appUrl, owner, isLoggedIn: true, isLoading: false }));
    setAppKey(appKey);
    setAppUrl(appUrl);
    setOwner(owner);
  },
});

/**
 * Creates a global zustand store.
 *
 * Despite [zustand recommendations](https://docs.pmnd.rs/zustand/guides/nextjs) for not using global stores in NextJs.
 * This store is global, because it encapsulates a local storage for appKey and appUrl, which is never available for SSR.
 */
const createUserDataStore = () => {
  const boundStore = create(subscribeWithSelector(store));

  if (IS_RUNNING_SERVER_SIDE) {
    return boundStore;
  }

  // Temporary disabled
  // window.addEventListener(
  //   'storage',
  //   (event: StorageEvent) => {
  //     const isAppKeyChange = event.storageArea === localStorage && event.key === LOCAL_STORAGE_KEYS.appKey;

  //     if (isAppKeyChange) {
  //       const newAppKey = event.newValue;
  //       const currentAppKey = useUserDataStore.getState().appKey;
  //       if (newAppKey && newAppKey !== currentAppKey) {
  //         useUserDataStore.getState().setAppKey(newAppKey);
  //       }
  //     }

  //     const isAppUrlChange = event.storageArea === localStorage && event.key === LOCAL_STORAGE_KEYS.appUrl;

  //     if (isAppUrlChange) {
  //       const newAppUrl = event.newValue;
  //       const currentAppUrl = useUserDataStore.getState().appUrl;
  //       if (newAppUrl && newAppUrl !== currentAppUrl) {
  //         useUserDataStore.getState().setAppUrl(newAppUrl);
  //       }
  //     }

  //     const isOwnerChange = event.storageArea === localStorage && event.key === LOCAL_STORAGE_KEYS.owner;

  //     if (isOwnerChange) {
  //       const newOwner = event.newValue;
  //       const currentOwner = useUserDataStore.getState().owner;
  //       if (newOwner && newOwner !== currentOwner) {
  //         useUserDataStore.getState().setOwner(newOwner);
  //       }
  //     }
  //   },
  //   false
  // );

  return boundStore;
};

export const useUserDataStore = createUserDataStore();

export const useCurrentApp = () => {
  return useUserDataStore(
    useShallow((state) => ({
      appKey: state.appKey,
      appUrl: state.appUrl,
      owner: state.owner,
      isLoggedIn: state.isLoggedIn,
      isLoading: state.isLoading,
    }))
  );
};
